/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, OnInit} from '@angular/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';

import {statComparator} from '../common/controller/comparators';
import {LocalCreativePreview} from '../../components/session/local-creative-preview-data';
import {Uuid} from '../../components/util/uuid';
import {collateCreativePreviewLineItemData} from '../creative/ad/preview-request-data';
import {CreativeResource} from '../../components/api/resources/resource/creative.resource';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-ad-unit-creative-totals-preview-modal',
  template: `
    <form class="form-horizontal" (ngSubmit)="dismiss()">
      <div class="modal-header dialogAddStuff">
        <button type="button" class="close" (click)="dismiss()">&times;</button>
        <h4 class="modal-title">Preview of Creatives</h4>
      </div>
      <div class="modal-body">
        <adn-promise-spinner [resolved]="!spinner" [andHide]="true">
          <p *ngIf="!sortedCreativeInfo.length" class="alert alertSimple">No creatives are available to preview</p>
          <table *ngIf="sortedCreativeInfo.length" class="table table-hover table-bordered table-condensed creativePreviewModalTable">
            <thead>
              <tr>
                <th>Creative Info and Preview</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let creativeInfo of sortedCreativeInfo; let i = index">
                <td>
                  <span *ngIf="creativeInfo.advertiser?.name">
                    <a (click)="dismiss()" [uiSref]="'app.adv.advertisers.advertiser'" [uiParams]="{id: creativeInfo.advertiser.id}">{{ creativeInfo.advertiser.name }}</a>
                    <span class="fa fa-fw fa-angle-right"></span>
                  </span>
                  <span *ngIf="creativeInfo.order?.name">
                    <a (click)="dismiss()" [uiSref]="'app.adv.orders.order'" [uiParams]="{id: creativeInfo.order.id}">{{ creativeInfo.order.name }}</a>
                    <span class="fa fa-fw fa-angle-right"></span>
                  </span>
                  <span *ngIf="creativeInfo.lineItem?.name">
                    <a (click)="dismiss()" [uiSref]="'app.adv.line-items.line-item'" [uiParams]="{id: creativeInfo.lineItem.id}">{{ creativeInfo.lineItem.name }}</a>
                    <span class="fa fa-fw fa-angle-right"></span>
                  </span>
                  <a (click)="dismiss()" [uiSref]="'app.adv.creatives.creative'" [uiParams]="{id: creativeInfo.creative.id}">{{ creativeInfo.creative.name }}</a>
                  <div *ngIf="creativeInfo.creative.type === 'EXTERNAL' || creativeInfo.creative.type === 'SSP'">
                    <p class="alert alertSimple">Cannot preview an external creative.</p>
                  </div>
                  <div *ngIf="creativeInfo.creative.type === 'INTERNAL' || !creativeInfo.creative.type">
                    <div *ngIf="i >= max">
                      <p>Only previewing the top {{ max }} creatives.</p>
                      <p><a (click)="max = max + step">Preview the next {{ step }} creatives</a></p>
                    </div>
                    <div *ngIf="i < max">
                      <adn-creative-dims-output [creative]="creativeInfo.creative"></adn-creative-dims-output>
                      <label class="simpleLabel">
                        <input type="checkbox" [(ngModel)]="creativeInfo.creative.allowScript" (ngModelChange)="preview(creativeInfo.creative)">
                        Enable JavaScript and links
                      </label>&nbsp;|&nbsp;
                      <a (click)="setCookieInfo(creativeInfo.creative)" [attr.href]="blankPageHref(creativeInfo.creative)" target="_blank">Preview on blank page</a>
                      <div class="creativePreviewModalViewport">
                        <adn-creative-direct [creative]="creativeInfo.creative"></adn-creative-direct>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </adn-promise-spinner>
      </div>
      <div class="modal-footer">
        <a class="btn btn-link btn-xs" (click)="dismiss()">{{ 'button.cancel' | ogTranslate }}</a>
      </div>
    </form>
  `
})
export class AdUnitCreativeTotalsPreviewModalComponent implements OnInit {
  totalsData: any[] = [];
  creativeSorting: any = {orderByParam: 'impressions', asc: true};

  spinner = true;
  creativeInfo: any[] = [];
  max = 10;
  step = 10;

  constructor(
    public bsModalRef: BsModalRef,
    private readonly cdr: ChangeDetectorRef,
    @Inject(CreativeResource) private readonly Creative: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly $state: StateService
  ) {
  }

  ngOnInit(): void {
    const creativeIds = _.map(this.totalsData || [], (datum) => datum.creative.id);
    const keyedTotalsData = _.keyBy(this.totalsData || []);
    if (!creativeIds.length) {
      this.spinner = false;
      return;
    }
    this.Creative.info({id: creativeIds.join(';'), minimal: true}).$promise.then((data: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.creativeInfo = data.results || [];
        _.forEach(this.creativeInfo, (c) => {
          c.creative.allowScript = false;
          c.creative.uuid = Uuid.generate();
          if (keyedTotalsData[c.id]) {
            _.assign(c, keyedTotalsData[c.id]);
          }
          c.redrawHook = {};
        });
        this.spinner = false;
      });
    }).catch(() => {
      deferMarkForCheck(this.cdr, () => {
        this.spinner = false;
      });
    });
  }

  get sortedCreativeInfo(): any[] {
    const orderByParam = this.creativeSorting?.orderByParam || 'impressions';
    const asc = !!this.creativeSorting?.asc;
    const info = [...(this.creativeInfo || [])];
    info.sort((a, b) => {
      const v1 = {value: _.get(a, orderByParam)};
      const v2 = {value: _.get(b, orderByParam)};
      let result = statComparator(v1, v2);
      if (asc) {
        result = -result;
      }
      return result;
    });
    return info;
  }

  preview(creative: any): void {
    if (creative?.redrawHook?.triggerPreview) {
      creative.redrawHook.triggerPreview(creative);
    }
  }

  setCookieInfo(creative: any): void {
    const previewData = {
      networkId: this.LocalNetworkProfile.getNetworkTag(),
      creativeId: creative.id,
      creativeWidth: creative.width,
      creativeHeight: creative.height
    };
    _.assign(previewData, collateCreativePreviewLineItemData(creative));
    new LocalCreativePreview().add(creative.uuid, this.LocalNetworkProfile.getNetworkTag(), previewData);
  }

  blankPageHref(creative: any): string {
    return this.$state.href('nude.preview', {uuid: creative?.uuid}, {absolute: true}) || '';
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
